import { Tenant, TenantUserData } from './types';

/**
 * TenantManager - Gère l'isolation et l'accès aux données multi-tenant
 * 
 * Cette classe fournit des méthodes pour gérer les tenants et l'accès aux données
 * tout en maintenant l'isolation entre les différents tenants.
 */
export class TenantManager {
  private static instance: TenantManager;
  private currentTenantId: string | null = null;
  private availableTenants: Tenant[] = [];
  private userTenantData: TenantUserData | null = null;

  // Constructeur privé pour le pattern Singleton
  private constructor() {}

  /**
   * Obtient l'instance unique du TenantManager (Singleton)
   */
  public static getInstance(): TenantManager {
    if (!TenantManager.instance) {
      TenantManager.instance = new TenantManager();
    }
    return TenantManager.instance;
  }

  /**
   * Initialise le TenantManager avec les données de l'utilisateur
   */
  public initialize(userTenantData: TenantUserData, tenants: Tenant[]): void {
    this.userTenantData = userTenantData;
    this.availableTenants = tenants;
    
    // Définir le tenant par défaut (le premier de la liste ou celui spécifié comme principal)
    if (userTenantData.tenantId && this.isTenantAccessible(userTenantData.tenantId)) {
      this.currentTenantId = userTenantData.tenantId;
    } else if (tenants.length > 0) {
      this.currentTenantId = tenants[0].id;
    }

    // Stocker les informations dans le localStorage pour persistance
    this.persistTenantState();
  }

  /**
   * Vérifie si le TenantManager est initialisé
   */
  public isInitialized(): boolean {
    return this.userTenantData !== null && this.availableTenants.length > 0;
  }

  /**
   * Obtient l'ID du tenant actuel
   */
  public getCurrentTenantId(): string | null {
    // Si pas encore initialisé, essayer de récupérer depuis localStorage
    if (!this.currentTenantId) {
      this.restoreTenantState();
    }
    return this.currentTenantId;
  }

  /**
   * Définit le tenant actuel
   */
  public setCurrentTenant(tenantId: string | undefined): boolean {
    // Vérifier si le tenantId est valide
    if (!tenantId) {
      console.warn('TenantManager: Tentative de définir un tenant avec un ID invalide ou undefined');
      
      // Si nous avons des tenants disponibles, essayer d'utiliser le premier
      if (this.userTenantData && this.userTenantData.tenants && 
          Array.isArray(this.userTenantData.tenants) && 
          this.userTenantData.tenants.length > 0 && 
          this.userTenantData.tenants[0] && 
          this.userTenantData.tenants[0].tenantId) {
        
        const firstTenantId = this.userTenantData.tenants[0].tenantId;
        console.log(`TenantManager: Utilisation du premier tenant disponible: ${firstTenantId}`);
        return this.setCurrentTenant(firstTenantId);
      }
      
      // Sinon, utiliser un tenant par défaut si disponible
      if (this.userTenantData && this.userTenantData.tenantId) {
        console.log(`TenantManager: Utilisation du tenant par défaut: ${this.userTenantData.tenantId}`);
        tenantId = this.userTenantData.tenantId;
      } else {
        // Aucun tenant disponible
        return false;
      }
    }

    // Vérifier si l'utilisateur a accès à ce tenant
    if (!this.isTenantAccessible(tenantId)) {
      console.warn(`TenantManager: L'utilisateur n'a pas accès au tenant: ${tenantId}, tentative de fallback`);
      
      // Essayer de trouver un tenant accessible
      if (this.userTenantData && this.userTenantData.tenants && 
          Array.isArray(this.userTenantData.tenants) && 
          this.userTenantData.tenants.length > 0) {
        
        // Trouver le premier tenant accessible
        for (const tenant of this.userTenantData.tenants) {
          if (tenant && tenant.tenantId && this.isTenantAccessible(tenant.tenantId)) {
            console.log(`TenantManager: Utilisation d'un tenant alternatif: ${tenant.tenantId}`);
            this.currentTenantId = tenant.tenantId;
            this.persistTenantState();
            return true;
          }
        }
      }
      
      return false;
    }

    this.currentTenantId = tenantId;
    this.persistTenantState();
    return true;
  }

  /**
   * Obtient la liste des tenants accessibles à l'utilisateur
   */
  public getAccessibleTenants(): Tenant[] {
    return this.availableTenants;
  }

  /**
   * Vérifie si l'utilisateur a accès à un tenant spécifique
   */
  public isTenantAccessible(tenantId: string): boolean {
    // Vérifier si le tenantId est valide
    if (!tenantId || typeof tenantId !== 'string' || tenantId.trim() === '') {
      console.warn('🏘️ TenantManager - Tentative d\'accès à un tenant avec un ID invalide');
      return false;
    }

    // Si pas de données utilisateur, vérifier si nous sommes en mode développement
    if (!this.userTenantData) {
      console.warn('🏘️ TenantManager - Pas de données utilisateur disponibles pour vérifier l\'accès au tenant', tenantId);
      
      // En mode développement, autoriser l'accès pour faciliter les tests
      if (process.env.NODE_ENV === 'development') {
        console.warn('🏘️ TenantManager - Accès autorisé en mode développement malgré l\'absence de données utilisateur');
        return true;
      }
      return false;
    }

    // Vérifier si c'est le tenant principal
    if (this.userTenantData.tenantId === tenantId) {
      console.log(`🏘️ TenantManager - Accès autorisé au tenant principal: ${tenantId}`);
      return true;
    }

    // Vérifier dans la liste des tenants accessibles
    if (this.userTenantData.tenants && Array.isArray(this.userTenantData.tenants)) {
      // Afficher les tenants disponibles pour le débogage
      console.log(`🏘️ TenantManager - Vérification d'accès au tenant ${tenantId}. Tenants disponibles:`, 
        this.userTenantData.tenants.map(t => t?.tenantId || 'undefined'));
      
      // Vérifier si le tenant est dans la liste des tenants accessibles
      const hasAccess = this.userTenantData.tenants.some(t => t && t.tenantId === tenantId);
      
      // Si l'accès est refusé, enregistrer un avertissement pour le débogage
      if (!hasAccess) {
        console.warn(`🏘️ TenantManager - Accès refusé au tenant ${tenantId}. Tenant non trouvé dans la liste des tenants accessibles.`);
      } else {
        console.log(`🏘️ TenantManager - Accès autorisé au tenant ${tenantId}.`);
      }
      
      return hasAccess;
    }

    // Si nous arrivons ici, c'est qu'il n'y a pas de liste de tenants disponible
    console.warn('🏘️ TenantManager - Aucune liste de tenants disponible pour vérifier l\'accès');
    return false;
  }

  /**
   * Obtient le rôle de l'utilisateur dans un tenant spécifique
   */
  public getUserRoleInTenant(tenantId: string): string | null {
    if (!this.userTenantData) return null;

    // Vérifier si c'est le tenant principal
    if (this.userTenantData.tenantId === tenantId) {
      return this.userTenantData.tenantRole;
    }

    // Vérifier dans la liste des tenants accessibles
    if (this.userTenantData.tenants) {
      const tenantInfo = this.userTenantData.tenants.find(t => t.tenantId === tenantId);
      return tenantInfo ? tenantInfo.role : null;
    }

    return null;
  }
  
  /**
   * Obtient les données utilisateur du tenant
   */
  public getUserTenantData(): TenantUserData | null {
    return this.userTenantData;
  }

  /**
   * Met à jour un tenant dans le cache local et persiste l'état.
   * @param updatedTenant Le tenant mis à jour.
   */
  public updateTenantInCache(updatedTenant: Tenant): void {
    if (!updatedTenant || !updatedTenant.id) {
      console.warn('TenantManager: Tentative de mise à jour du cache avec un tenant invalide.');
      return;
    }

    const index = this.availableTenants.findIndex(t => t.id === updatedTenant.id);
    if (index !== -1) {
      this.availableTenants[index] = updatedTenant;
      console.log(`TenantManager: Tenant ${updatedTenant.id} mis à jour dans le cache.`);
      this.persistTenantState(); // Sauvegarder les modifications
    } else {
      // Si le tenant n'est pas dans la liste, on pourrait choisir de l'ajouter
      // ou de simplement logger un avertissement. Pour l'instant, on logue.
      console.warn(`TenantManager: Tenant ${updatedTenant.id} non trouvé dans availableTenants pour mise à jour du cache.`);
      // Optionnellement, ajouter le tenant s'il n'existe pas et qu'on veut le cacher quand même:
      // this.availableTenants.push(updatedTenant);
      // this.persistTenantState();
    }

    // Si le tenant mis à jour est le tenant actuel, s'assurer que currentTenantId est toujours valide
    // (normalement, l'ID ne change pas, mais d'autres propriétés oui)
    if (this.currentTenantId === updatedTenant.id) {
      // On pourrait vouloir rafraîchir des données spécifiques au tenant actuel si nécessaire
    }
  }

  /**
   * Ajoute le contexte de tenant aux paramètres de requête API
   */
  public addTenantContext<T>(params: T): T & { tenantId: string } {
    const tenantId = this.getCurrentTenantId();
    if (!tenantId) {
      throw new Error('Aucun tenant actif. Veuillez initialiser le TenantManager.');
    }
    return { ...params, tenantId };
  }

  /**
   * Filtre les données par tenant pour garantir l'isolation
   */
  public filterDataByTenant<T extends { tenantId?: string }>(data: T[]): T[] {
    const tenantId = this.getCurrentTenantId();
    if (!tenantId) return [];
    
    return data.filter(item => !item.tenantId || item.tenantId === tenantId);
  }
  
  /**
   * Compte le nombre de tenants gratuits de l'utilisateur
   */
  public countFreeTenants(): number {
    return this.availableTenants.filter(tenant => tenant.plan === 'free').length;
  }
  
  /**
   * Vérifie si l'utilisateur peut créer un nouveau tenant gratuit
   * @returns {boolean} true si l'utilisateur peut créer un tenant gratuit, false sinon
   */
  public canCreateFreeTenant(): boolean {
    // Limite à 1 tenant gratuit par utilisateur
    return this.countFreeTenants() < 1;
  }

  /**
   * Réinitialise le TenantManager (utile lors de la déconnexion)
   */
  public reset(): void {
    this.currentTenantId = null;
    this.availableTenants = [];
    this.userTenantData = null;
    localStorage.removeItem('currentTenantId');
    localStorage.removeItem('tenantData');
  }

  /**
   * Persiste l'état du tenant dans le localStorage
   */
  private persistTenantState(): void {
    if (typeof window === 'undefined') return; // Vérifier si nous sommes dans un environnement navigateur
    
    console.log('TenantManager: Persistance des données dans le localStorage');
    console.log('TenantManager: Tenants disponibles:', this.availableTenants);
    
    // Toujours sauvegarder les tenants disponibles, même s'il n'y a pas de tenant actif
    localStorage.setItem('availableTenants', JSON.stringify(this.availableTenants));
    
    if (this.currentTenantId) {
      localStorage.setItem('currentTenantId', this.currentTenantId);
    }
    
    if (this.userTenantData) {
      localStorage.setItem('tenantData', JSON.stringify({
        userTenantData: this.userTenantData,
        availableTenants: this.availableTenants
      }));
    }
    
    // Déclencher un événement personnalisé pour informer les autres composants du changement
    const event = new CustomEvent('tenantDataChanged', { 
      detail: { 
        tenants: this.availableTenants,
        currentTenantId: this.currentTenantId 
      } 
    });
    window.dispatchEvent(event);
  }

  /**
   * Restaure l'état du tenant depuis le localStorage
   */
  private restoreTenantState(): void {
    if (typeof window === 'undefined') return; // Vérifier si nous sommes dans un environnement navigateur
    
    console.log('TenantManager: Restauration des données depuis le localStorage');
    
    // Essayer de récupérer les tenants disponibles directement
    const storedAvailableTenants = localStorage.getItem('availableTenants');
    if (storedAvailableTenants) {
      try {
        this.availableTenants = JSON.parse(storedAvailableTenants);
        console.log('TenantManager: Tenants restaurés depuis availableTenants:', this.availableTenants);
      } catch (error) {
        console.error('Erreur lors de la restauration des tenants disponibles:', error);
      }
    }
    
    // Récupérer l'ID du tenant actuel
    const storedTenantId = localStorage.getItem('currentTenantId');
    if (storedTenantId) {
      this.currentTenantId = storedTenantId;
      console.log('TenantManager: Tenant actuel restauré:', this.currentTenantId);
    }

    // Récupérer les données de tenant complètes
    const storedTenantData = localStorage.getItem('tenantData');
    if (storedTenantData) {
      try {
        const { userTenantData, availableTenants } = JSON.parse(storedTenantData);
        this.userTenantData = userTenantData;
        
        // Si nous n'avons pas déjà restauré les tenants disponibles, utiliser ceux-ci
        if (!this.availableTenants.length && availableTenants && availableTenants.length) {
          this.availableTenants = availableTenants;
          console.log('TenantManager: Tenants restaurés depuis tenantData:', this.availableTenants);
        }
      } catch (error) {
        console.error('Erreur lors de la restauration des données de tenant:', error);
      }
    }
    
    // Si nous avons des tenants mais pas de données utilisateur, créer des données par défaut
    if (this.availableTenants.length && !this.userTenantData) {
      this.userTenantData = {
        tenantId: this.availableTenants[0].id,
        tenantRole: 'admin',
        tenants: this.availableTenants.map(t => ({ tenantId: t.id, role: 'admin' }))
      };
      console.log('TenantManager: Données utilisateur créées par défaut:', this.userTenantData);
    }
  }
}

// Exporter une instance unique
export const tenantManager = TenantManager.getInstance();

export default tenantManager;
